/*
Origin Core 1.0

called from /sections/product-recommendations.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <product-recommendations> that lazily loads product recommendations when the component enters the viewport. 
It uses an IntersectionObserver to trigger loading, fetches recommendations via AJAX, and caches results for efficiency. 
The component updates its content with the fetched recommendations and initializes a Splide carousel for displaying recommended products. 
It also observes attribute changes to reload recommendations when needed and handles errors gracefully. 
The component is registered if not already defined.

*/

class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;

          const sectionId = this.dataset.sectionId;

          const initCarousel = () => {
            const productRecsSlideshow = new Splide(`[data-product-carousel-${sectionId}]`, {
              autoWidth: true,
              /**
               * With autoWidth, Splide can keep the next arrow enabled on the last "page" while it only
               * advances the active slide without scrolling. focus + omitEnd (Splide >=4.1) disables the
               * next control when no further scroll is possible — see splidejs.com/guides/auto-width/
               */
              focus: 0,
              omitEnd: true,
              perMove: 1,
              easingFunc: true,
              pagination: false,
              type: 'slide',
              drag: true,
              arrows: true,
              gap: 20,
              padding: { left: '24px', right: '24px' },
              breakpoints: {
                767: {
                  padding: { left: '16px', right: '16px' }
                }
              }
            });

            const bar = productRecsSlideshow.root.querySelector('.my-slider-progress-bar');

            /** Use Move positions so progress reflects real scroll distance. */
            const carouselProgressRate = function (splide) {
              const Move = splide.Components.Move;
              const Controller = splide.Components.Controller;
              if (!Move || !Controller) return 1;

              const start = Move.toPosition(0, true);
              const end = Move.toPosition(Controller.getEnd(), true);
              const current = Move.getPosition();
              const denom = end - start;
              if (Math.abs(denom) < 0.5) return 1;

              const raw = (current - start) / denom;
              if (Number.isNaN(raw)) return 1;
              return Math.min(Math.max(raw, 0), 1);
            };

            const updateBar = function () {
              if (!bar) return;
              bar.style.width = String(100 * carouselProgressRate(productRecsSlideshow)) + '%';
            };

            /** Keep next-arrow state synced with real terminal position. */
            const syncNextArrowWithProductProgress = function () {
              setTimeout(function () {
                const nextBtn = productRecsSlideshow.root.querySelector('.splide__arrow--next');
                if (!nextBtn) return;
                const track = productRecsSlideshow.root.querySelector('.splide__track');
                const list = productRecsSlideshow.root.querySelector('.splide__list');
                const isOverflow = !!track && !!list && list.scrollWidth > track.clientWidth + 1;
                const p = carouselProgressRate(productRecsSlideshow);
                const EPS = 0.002;
                nextBtn.disabled = !isOverflow || p >= 1 - EPS;
              }, 0);
            };

            productRecsSlideshow.on('mounted move moved resized dragged', function () {
              updateBar();
              syncNextArrowWithProductProgress();
            });

            const updateOverflow = function () {
              const track = productRecsSlideshow.root.querySelector('.splide__track');
              const list = productRecsSlideshow.root.querySelector('.splide__list');
              if (!track || !list) {
                productRecsSlideshow.root.classList.toggle(
                  'is-overflow',
                  productRecsSlideshow.Components.Controller.getEnd() > 0
                );
                return;
              }
              const isOverflow = list.scrollWidth > track.clientWidth + 1;
              productRecsSlideshow.root.classList.toggle('is-overflow', isOverflow);
            };

            productRecsSlideshow.on('mounted resized', function () {
              updateOverflow();
              requestAnimationFrame(updateOverflow);
            });

            productRecsSlideshow.mount();
          };

          // Splide is loaded with `defer` — wait for it to be available before mounting.
          if (window.Splide) {
            initCarousel();
          } else {
            const splideCheck = setInterval(() => {
              if (window.Splide) {
                clearInterval(splideCheck);
                initCarousel();
              }
            }, 50);
          }


        } else {
          this.classList.add('hidden');
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
